import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = "/api";
const AdminContext = createContext(null);

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
};

export const AdminProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const [bandRegistrations, setBandRegistrations] = useState([]);
  const [venueRegistrations, setVenueRegistrations] = useState([]);
  const [promoterRegistrations, setPromoterRegistrations] = useState([]);
  const [contentCreatorRegistrations, setContentCreatorRegistrations] = useState([]);
  const [growerRegistrations, setGrowerRegistrations] = useState([]);
  const [approvedGrowers, setApprovedGrowers] = useState([]);
  const [extractorRegistrations, setExtractorRegistrations] = useState([]);
  const [dispensaryRegistrations, setDispensaryRegistrations] = useState([]);
  const [eventSubmissions, setEventSubmissions] = useState([]);
  const [featureRequests, setFeatureRequests] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [artists, setArtists] = useState([]);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [localBusinesses, setLocalBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [ads, setAds] = useState([]);
  const [adStats, setAdStats] = useState(null);
  const [siteStats, setSiteStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [bandSubmittedShorts, setBandSubmittedShorts] = useState([]);
  const [bandSubmittedEvents, setBandSubmittedEvents] = useState([]);
  const [homepageSections, setHomepageSections] = useState([]);
  const [homepageContent, setHomepageContent] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [savingLayout, setSavingLayout] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [adDialogOpen, setAdDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [newBusiness, setNewBusiness] = useState({
    name: '', category: 'restaurant', description: '', address: '',
    city: 'Minneapolis', phone: '', website: '', image_url: ''
  });
  const [newAd, setNewAd] = useState({
    title: '', business_name: '', description: '', image_url: '',
    link_url: '', is_premium: false, is_active: true, placement: 'sidebar',
    start_date: '', end_date: '', max_impressions: '', max_clicks: ''
  });
  const [credentialsForm, setCredentialsForm] = useState({
    currentPassword: '', newEmail: '', newPassword: '', confirmPassword: ''
  });
  const [changingCredentials, setChangingCredentials] = useState(false);

  useEffect(() => {
    const storedKey = localStorage.getItem('adminKey');
    if (storedKey) {
      setAdminKey(storedKey);
      verifyAdminKey(storedKey);
    }
  }, []);

  const verifyAdminKey = async (key) => {
    try {
      await axios.get(`${API}/admin/verify`, { headers: { 'X-Admin-Key': key } });
      setIsLoggedIn(true);
      setAdminKey(key);
      fetchAllData(key);
    } catch {
      localStorage.removeItem('adminKey');
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setAdminKey(response.data.admin_key);
        localStorage.setItem('adminKey', response.data.admin_key);
        setIsLoggedIn(true);
        toast.success('Admin login successful!');
        fetchAllData(response.data.admin_key);
      }
    } catch {
      toast.error('Invalid password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminKey');
    setIsLoggedIn(false);
    setAdminKey('');
    toast.success('Logged out');
  };

  const fetchAllData = async (key) => {
    setLoading(true);
    const headers = { 'X-Admin-Key': key };
    try {
      const [statsRes, bandsRes, venuesRes, promotersRes, creatorsRes, growersRes, extractorsRes, eventsRes, featuresRes, messagesRes, artistsRes, trucksRes, vendorsRes, businessesRes, usersRes, activityRes, adsRes, adStatsRes, layoutRes, contentRes, bandShortsRes, bandEventsRes, approvedGrowersRes, siteStatsRes, dispensaryRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/band-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/venue-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/promoter-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/content-creator-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/grower-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/extractor-registrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/event-submissions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/feature-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/contact-messages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/artists`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/food-trucks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/vendors`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/businesses`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/activity`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/ads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/ads/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/homepage/layout`, { headers }).catch(() => ({ data: { sections: [] } })),
        axios.get(`${API}/admin/homepage/content`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/band-submitted-shorts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/band-submitted-events`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/growers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/site-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/dispensary-registrations`, { headers }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setBandRegistrations(bandsRes.data);
      setVenueRegistrations(venuesRes.data);
      setPromoterRegistrations(promotersRes.data);
      setContentCreatorRegistrations(creatorsRes.data);
      setGrowerRegistrations(growersRes.data);
      setApprovedGrowers(approvedGrowersRes.data);
      setExtractorRegistrations(extractorsRes.data);
      setDispensaryRegistrations(dispensaryRes.data);
      setEventSubmissions(eventsRes.data);
      setFeatureRequests(featuresRes.data);
      setContactMessages(messagesRes.data);
      setArtists(artistsRes.data);
      setFoodTrucks(trucksRes.data);
      setVendors(vendorsRes.data);
      setLocalBusinesses(businessesRes.data);
      setUsers(usersRes.data);
      setRecentActivity(activityRes.data);
      setAds(adsRes.data);
      setAdStats(adStatsRes.data);
      setHomepageSections(layoutRes.data.sections || []);
      setHomepageContent(contentRes.data);
      setBandSubmittedShorts(bandShortsRes.data);
      setBandSubmittedEvents(bandEventsRes.data);
      setSiteStats(siteStatsRes.data);

      const schedulerRes = await axios.get(`${API}/admin/scheduler/status`, { headers }).catch(() => ({ data: null }));
      setSchedulerStatus(schedulerRes.data);
      const revenueRes = await axios.get(`${API}/admin/revenue`, { headers }).catch(() => ({ data: null }));
      setRevenueData(revenueRes.data);
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => fetchAllData(adminKey);

  // Push notifications
  const checkPushStatus = async () => {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      const permission = Notification.permission;
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushEnabled(!!subscription);
      }
    }
  };

  const enablePushNotifications = async () => {
    if (!('Notification' in window)) { toast.error('Push notifications are not supported in this browser'); return; }
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { toast.error('Notification permission denied'); setPushLoading(false); return; }
      const keyRes = await axios.get(`${API}/admin/vapid-public-key`);
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
        return outputArray;
      };
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey) });
      await axios.post(`${API}/admin/push-subscribe`, { subscription: subscription.toJSON() }, { headers: { 'X-Admin-Key': adminKey } });
      setPushEnabled(true);
      toast.success('Push notifications enabled!');
    } catch (error) { console.error('Push setup error:', error); toast.error('Failed to enable push notifications'); }
    finally { setPushLoading(false); }
  };

  const disablePushNotifications = async () => {
    setPushLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await axios.delete(`${API}/admin/push-unsubscribe`, { data: { subscription: subscription.toJSON() }, headers: { 'X-Admin-Key': adminKey } });
        await subscription.unsubscribe();
      }
      setPushEnabled(false);
      toast.success('Push notifications disabled');
    } catch { toast.error('Failed to disable push notifications'); }
    finally { setPushLoading(false); }
  };

  const testPushNotification = async () => {
    try { await axios.post(`${API}/admin/test-push`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Test notification sent!'); }
    catch { toast.error('Failed to send test notification'); }
  };

  useEffect(() => { if (isLoggedIn) checkPushStatus(); }, [isLoggedIn]);

  // Homepage layout
  const moveSection = (index, direction) => {
    const newSections = [...homepageSections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    const tempOrder = newSections[index].order;
    newSections[index].order = newSections[newIndex].order;
    newSections[newIndex].order = tempOrder;
    newSections.sort((a, b) => a.order - b.order);
    setHomepageSections(newSections);
  };

  const toggleSectionVisibility = (index) => {
    const newSections = [...homepageSections];
    newSections[index].visible = !newSections[index].visible;
    setHomepageSections(newSections);
  };

  const saveHomepageLayout = async () => {
    setSavingLayout(true);
    try { await axios.put(`${API}/admin/homepage/layout`, { sections: homepageSections }, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Homepage layout saved!'); }
    catch { toast.error('Failed to save layout'); }
    finally { setSavingLayout(false); }
  };

  const updateContentImage = async (contentType, contentId, imageUrl) => {
    try {
      await axios.put(`${API}/admin/content/image`, { content_type: contentType, content_id: contentId, image_url: imageUrl }, { headers: { 'X-Admin-Key': adminKey } });
      toast.success('Image updated!');
      setEditingImage(null);
      setNewImageUrl('');
      refreshData();
    } catch { toast.error('Failed to update image'); }
  };

  // API helpers
  const apiCall = async (method, endpoint, data = null) => {
    const config = { headers: { 'X-Admin-Key': adminKey } };
    try {
      if (method === 'post') await axios.post(`${API}${endpoint}`, data, config);
      else if (method === 'put') await axios.put(`${API}${endpoint}`, data, config);
      else if (method === 'delete') await axios.delete(`${API}${endpoint}`, config);
      toast.success('Action completed');
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Action failed'); }
  };

  // Registration handlers
  const updateRegistrationStatus = async (type, id, status) => {
    try {
      const action = status === 'approved' || status === 'published' ? 'approve' : 'reject';
      await axios.post(`${API}/admin/${type}/${id}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } });
      toast.success(`Registration ${action}d successfully!`);
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update registration'); }
  };

  const deleteRegistration = async (type, id) => {
    if (!confirm('Are you sure you want to delete this registration?')) return;
    try { await axios.delete(`${API}/admin/${type}/${id}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Registration deleted'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete registration'); }
  };

  const featureItem = (type, id) => apiCall('post', `/admin/feature/${type}/${id}`);
  const unfeatureItem = (type, id) => apiCall('delete', `/admin/feature/${type}/${id}`);
  const banItem = (type, id) => apiCall('post', `/admin/ban/${type}/${id}`);
  const unbanItem = (type, id) => apiCall('post', `/admin/unban/${type}/${id}`);

  const handleGrowerApproval = async (registrationId, action) => {
    try { await axios.post(`${API}/admin/grower-registrations/${registrationId}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(action === 'approve' ? 'Grower approved!' : 'Grower rejected'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} grower`); }
  };

  const syncGrowerRegistration = async (registrationId) => {
    try { const res = await axios.post(`${API}/admin/grower-registrations/${registrationId}/sync`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(res.data.message); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to sync grower'); }
  };

  const fixGrowerVisibility = async () => {
    try { const res = await axios.post(`${API}/admin/growers/fix-visibility`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(res.data.message); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to fix grower visibility'); }
  };

  const handleExtractorApproval = async (registrationId, action) => {
    try { await axios.post(`${API}/admin/extractor-registrations/${registrationId}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(action === 'approve' ? 'Extractor approved!' : 'Extractor rejected'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} extractor`); }
  };

  const handleDispensaryApproval = async (registrationId, action) => {
    try { await axios.post(`${API}/admin/dispensary-registrations/${registrationId}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(action === 'approve' ? 'Dispensary approved!' : 'Dispensary rejected'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} dispensary`); }
  };

  const deleteDispensaryRegistration = async (registrationId) => {
    if (!confirm('Delete this dispensary registration?')) return;
    try { await axios.delete(`${API}/admin/dispensary-registrations/${registrationId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Registration deleted'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete registration'); }
  };

  const deleteGrower = async (growerId) => {
    if (!confirm('Delete this grower permanently?')) return;
    try { await axios.delete(`${API}/admin/growers/${growerId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Grower deleted'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete grower'); }
  };

  const deleteGrowerRegistration = async (registrationId) => {
    if (!confirm('Delete this grower registration?')) return;
    try { await axios.delete(`${API}/admin/grower-registrations/${registrationId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Registration deleted'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete registration'); }
  };

  const deleteExtractor = async (extractorId) => {
    if (!confirm('Delete this extractor permanently?')) return;
    try { await axios.delete(`${API}/admin/extractors/${extractorId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Extractor deleted'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete extractor'); }
  };

  const deleteExtractorRegistration = async (registrationId) => {
    if (!confirm('Delete this extractor registration?')) return;
    try { await axios.delete(`${API}/admin/extractor-registrations/${registrationId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Registration deleted'); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete registration'); }
  };

  const handleBandShortAction = async (shortId, action) => {
    try {
      if (action === 'delete') { await axios.delete(`${API}/admin/band-submitted-shorts/${shortId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Short deleted'); }
      else { await axios.post(`${API}/admin/band-submitted-shorts/${shortId}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(action === 'approve' ? 'Short approved!' : 'Short rejected'); }
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} short`); }
  };

  const handleBandEventAction = async (eventId, action) => {
    try {
      if (action === 'delete') { await axios.delete(`${API}/admin/band-submitted-events/${eventId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Event deleted'); }
      else { await axios.post(`${API}/admin/band-submitted-events/${eventId}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(action === 'approve' ? 'Event approved!' : 'Event rejected'); }
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} event`); }
  };

  const handleBandRegistrationAction = async (registrationId, action) => {
    try {
      if (action === 'delete') { await axios.delete(`${API}/admin/band-registrations/${registrationId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Registration deleted'); }
      else {
        const res = await axios.post(`${API}/admin/band-registrations/${registrationId}/${action}`, {}, { headers: { 'X-Admin-Key': adminKey } });
        if (action === 'approve') toast.success(`Artist approved! Profile created (ID: ${res.data.artist_id})`);
        else toast.success('Artist registration rejected');
      }
      refreshData();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} artist registration`); }
  };

  const cleanupTestData = async () => {
    if (!confirm('This will permanently delete ALL test data. Continue?')) return;
    try { const res = await axios.post(`${API}/admin/cleanup-test-data`, {}, { headers: { 'X-Admin-Key': adminKey } }); const totalDeleted = Object.values(res.data.results).reduce((sum, count) => sum + count, 0); toast.success(`Cleaned up ${totalDeleted} test items`); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to cleanup test data'); }
  };

  const syncVenueEvents = async () => {
    try { toast.info("Syncing events from Gluek's, Day Block, and Green Room..."); const res = await axios.post(`${API}/admin/events/sync-venues`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(`Sync complete! Added: ${res.data.events_added}, Updated: ${res.data.events_updated}, Past removed: ${res.data.past_events_removed}`); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to sync events'); }
  };

  const cleanupPastEvents = async () => {
    try { const res = await axios.post(`${API}/admin/events/cleanup-past`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success(res.data.message); refreshData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to cleanup past events'); }
  };

  const initializeDatabase = async () => {
    if (!confirm('Re-seed database with canonical content? This is safe and will not delete existing data.')) return;
    try {
      toast.info('Initializing database...');
      const res = await axios.post(`${API}/admin/initialize-database`, {}, { headers: { 'X-Admin-Key': adminKey } });
      if (res.data.status === 'success') { const c = res.data.counts; toast.success(`Database initialized! Events: ${c.events}, Vendors: ${c.vendors}, Tracks: ${c.tracks}, Artists: ${c.artists}`); refreshData(); }
      else toast.error(res.data.message || 'Initialization failed');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to initialize database'); }
  };

  const createBusiness = async () => {
    try { await axios.post(`${API}/admin/business`, newBusiness, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Business created'); setBusinessDialogOpen(false); setNewBusiness({ name: '', category: 'restaurant', description: '', address: '', city: 'Minneapolis', phone: '', website: '', image_url: '' }); refreshData(); }
    catch { toast.error('Failed to create business'); }
  };

  const deleteBusiness = (id) => apiCall('delete', `/admin/business/${id}`);

  const deleteEvent = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    try { await axios.delete(`${API}/admin/events/${eventId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Event deleted'); refreshData(); }
    catch { toast.error('Failed to delete event'); }
  };

  const deleteArtist = async (artistId) => {
    if (!confirm('Delete this artist?')) return;
    try { await axios.delete(`${API}/admin/artists/${artistId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Artist deleted'); refreshData(); }
    catch { toast.error('Failed to delete artist'); }
  };

  const deleteVendor = async (vendorId) => {
    if (!confirm('Delete this vendor?')) return;
    try { await axios.delete(`${API}/admin/vendors/${vendorId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Vendor deleted'); refreshData(); }
    catch { toast.error('Failed to delete vendor'); }
  };

  const deleteFoodTruck = async (truckId) => {
    if (!confirm('Delete this food truck?')) return;
    try { await axios.delete(`${API}/admin/food-trucks/${truckId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Food truck deleted'); refreshData(); }
    catch { toast.error('Failed to delete food truck'); }
  };

  // Ad management
  const handleCreateAd = async (e) => {
    e.preventDefault();
    try {
      const adData = { ...newAd, max_impressions: newAd.max_impressions ? parseInt(newAd.max_impressions) : null, max_clicks: newAd.max_clicks ? parseInt(newAd.max_clicks) : null, start_date: newAd.start_date || null, end_date: newAd.end_date || null };
      await axios.post(`${API}/admin/ads`, adData, { headers: { 'X-Admin-Key': adminKey } });
      toast.success('Ad created'); setAdDialogOpen(false);
      setNewAd({ title: '', business_name: '', description: '', image_url: '', link_url: '', is_premium: false, is_active: true, placement: 'sidebar', start_date: '', end_date: '', max_impressions: '', max_clicks: '' });
      refreshData();
    } catch { toast.error('Failed to create ad'); }
  };

  const handleUpdateAd = async (e) => {
    e.preventDefault();
    if (!editingAd) return;
    try {
      const adData = { ...newAd, max_impressions: newAd.max_impressions ? parseInt(newAd.max_impressions) : null, max_clicks: newAd.max_clicks ? parseInt(newAd.max_clicks) : null, start_date: newAd.start_date || null, end_date: newAd.end_date || null };
      await axios.put(`${API}/admin/ads/${editingAd.id}`, adData, { headers: { 'X-Admin-Key': adminKey } });
      toast.success('Ad updated'); setAdDialogOpen(false); setEditingAd(null);
      setNewAd({ title: '', business_name: '', description: '', image_url: '', link_url: '', is_premium: false, is_active: true, placement: 'sidebar', start_date: '', end_date: '', max_impressions: '', max_clicks: '' });
      refreshData();
    } catch { toast.error('Failed to update ad'); }
  };

  const toggleAd = async (adId) => {
    try { await axios.post(`${API}/admin/ads/${adId}/toggle`, {}, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Ad status updated'); refreshData(); }
    catch { toast.error('Failed to toggle ad'); }
  };

  const deleteAd = async (adId) => {
    if (!confirm('Delete this ad?')) return;
    try { await axios.delete(`${API}/admin/ads/${adId}`, { headers: { 'X-Admin-Key': adminKey } }); toast.success('Ad deleted'); refreshData(); }
    catch { toast.error('Failed to delete ad'); }
  };

  const openEditAd = (ad) => {
    setEditingAd(ad);
    setNewAd({ title: ad.title || '', business_name: ad.business_name || '', description: ad.description || '', image_url: ad.image_url || '', link_url: ad.link_url || '', is_premium: ad.is_premium || false, is_active: ad.is_active !== false, placement: ad.placement || 'sidebar', start_date: ad.start_date ? ad.start_date.split('T')[0] : '', end_date: ad.end_date ? ad.end_date.split('T')[0] : '', max_impressions: ad.max_impressions || '', max_clicks: ad.max_clicks || '' });
    setAdDialogOpen(true);
  };

  const handleChangeCredentials = async (e) => {
    e.preventDefault();
    if (credentialsForm.newPassword && credentialsForm.newPassword !== credentialsForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (credentialsForm.newPassword && credentialsForm.newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!credentialsForm.currentPassword) { toast.error('Current password is required'); return; }
    const userToken = localStorage.getItem('token');
    if (!userToken) { toast.error('Please login with your user account first'); return; }
    setChangingCredentials(true);
    try {
      const response = await axios.put(`${API}/users/credentials`, { current_password: credentialsForm.currentPassword, new_email: credentialsForm.newEmail || null, new_password: credentialsForm.newPassword || null }, { headers: { 'Authorization': `Bearer ${userToken}` } });
      if (response.data.token) localStorage.setItem('token', response.data.token);
      toast.success('Credentials updated!');
      setCredentialsForm({ currentPassword: '', newEmail: '', newPassword: '', confirmPassword: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update credentials'); }
    finally { setChangingCredentials(false); }
  };

  const value = {
    isLoggedIn, adminKey, password, setPassword, loginLoading, stats, activeTab, setActiveTab,
    pushEnabled, pushLoading, loading,
    bandRegistrations, venueRegistrations, promoterRegistrations, contentCreatorRegistrations,
    growerRegistrations, approvedGrowers, extractorRegistrations, dispensaryRegistrations, eventSubmissions,
    featureRequests, contactMessages, artists, foodTrucks, vendors, localBusinesses,
    users, recentActivity, ads, adStats, siteStats, schedulerStatus,
    bandSubmittedShorts, bandSubmittedEvents,
    homepageSections, homepageContent, editingImage, setEditingImage, newImageUrl, setNewImageUrl, savingLayout,
    revenueData, businessDialogOpen, setBusinessDialogOpen, adDialogOpen, setAdDialogOpen,
    editingAd, setEditingAd, newBusiness, setNewBusiness, newAd, setNewAd,
    credentialsForm, setCredentialsForm, changingCredentials,
    // Actions
    handleLogin, handleLogout, refreshData, fetchAllData,
    enablePushNotifications, disablePushNotifications, testPushNotification,
    moveSection, toggleSectionVisibility, saveHomepageLayout, updateContentImage,
    apiCall, updateRegistrationStatus, deleteRegistration,
    featureItem, unfeatureItem, banItem, unbanItem,
    handleGrowerApproval, syncGrowerRegistration, fixGrowerVisibility,
    handleExtractorApproval, deleteGrower, deleteGrowerRegistration, deleteExtractor, deleteExtractorRegistration,
    handleDispensaryApproval, deleteDispensaryRegistration,
    handleBandShortAction, handleBandEventAction, handleBandRegistrationAction,
    cleanupTestData, syncVenueEvents, cleanupPastEvents, initializeDatabase,
    createBusiness, deleteBusiness, deleteEvent, deleteArtist, deleteVendor, deleteFoodTruck,
    handleCreateAd, handleUpdateAd, toggleAd, deleteAd, openEditAd,
    handleChangeCredentials,
    adminKey,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};
